import React, { useState, useEffect } from "react";
import {
    Row, Col, Form, Label, Input, Card, CardBody,
    Button, FormFeedback, InputGroup
} from "reactstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import './CustomCheckbox.css';

const VehicleCreate = ({ onBacked }) => {
    const navigate = useNavigate();

    // 🔹 Form states
    const [makeName, setMakeName] = useState("");
    const [modelName, setModelName] = useState("");
    const [licensePlate, setLicensePlate] = useState("");
    const [typeId, setTypeId] = useState("");
    const [bodyType, setBodyType] = useState("");
    const [year, setYear] = useState(null);
    const [seats, setSeats] = useState(4);
    const [seatRate, setSeatRate] = useState("");
    const [images, setImages] = useState([]);
    const [previewUrls, setPreviewUrls] = useState([]);
    const [loading, setLoading] = useState(false);

    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [bodyTypes, setBodyTypes] = useState([]);

    const [validation, setValidation] = useState({
        makeName: null,
        modelName: null,
        licensePlate: null,
        typeId: null,
        year: null,
        bodyType: null,
        seats: null,
        seatRate: null,
    });

    const handleRemoveImage = (index) => {
        const newImages = images.filter((_, i) => i !== index);
        const newPreviews = previewUrls.filter((_, i) => i !== index);

        setImages(newImages);
        setPreviewUrls(newPreviews);
    };

    useEffect(() => {
        const fetchTypes = async () => {
            try {
                const token = localStorage.getItem("authToken");
                const res = await fetch(`${process.env.REACT_APP_WEB_APP_URL}/api/vehicle-types`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const data = await res.json();
                if (res.status === 200) setVehicleTypes(data?.data || []);
            } catch (err) {
                console.error(err);
                toast.error("Failed to load vehicle types");
            }
        };
        const fetchBodyTypes = async () => {
            try {
                const token = localStorage.getItem("authToken");
                const res = await fetch(`${process.env.REACT_APP_WEB_APP_URL}/api/get-vehicle-body-types`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const data = await res.json();
                if (res.status === 200) setBodyTypes(data?.data || []);
            } catch (err) {
                console.error(err);
                toast.error("Failed to load body types");
            }
        };
        fetchBodyTypes();
        fetchTypes();
    }, []);

    const validateFields = () => {
        const newValidation = {
            makeName: makeName.trim() !== "",
            modelName: modelName.trim() !== "",
            licensePlate: licensePlate.trim() !== "",
            typeId: typeId !== "",
            year: year !== "",
            bodyType: bodyType !== "",
            seats: Number(seats) > 0,
            seatRate: Number(seatRate) > 0,
        };
        setValidation(newValidation);
        return Object.values(newValidation).every(Boolean);
    };

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);
        setImages(files);
        setPreviewUrls(files.map((f) => URL.createObjectURL(f)));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateFields()) return;

        setLoading(true);
        const formData = new FormData();
        formData.append("make_name", makeName);
        formData.append("model_name", modelName);
        formData.append("license_plate", licensePlate);
        formData.append("type_id", typeId);
        formData.append("year", year instanceof Date ? year.getFullYear() : year);
        formData.append("body_type", bodyType);
        formData.append("seats", seats);
        formData.append("seat_rate", seatRate);
        images.forEach((img) => formData.append("images[]", img));

        try {
            const token = localStorage.getItem("authToken");
            const response = await fetch(`${process.env.REACT_APP_WEB_APP_URL}/api/add-vehicle`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
            });
            const data = await response.json();

            if (response.status === 200) {
                toast.success("Vehicle created successfully");
                setTimeout(() => {
                    onBacked();
                }, 800);
            } else {
                toast.error(data?.message || "Failed to create vehicle");
            }
        } catch (err) {
            console.error(err);
            toast.error("Error creating vehicle");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container">
            <h3 className="mb-4">Add New Vehicle</h3>
            <Card className="p-4 shadow-sm">
                <CardBody>
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col md="6">
                                <Label>Make Name</Label>
                                <Input
                                    type="text"
                                    placeholder="Enter make name"
                                    value={makeName}
                                    onChange={(e) => setMakeName(e.target.value)}
                                    valid={validation.makeName === true}
                                    invalid={validation.makeName === false}
                                />
                                <FormFeedback>Make name is required.</FormFeedback>
                            </Col>
                            <Col md="6">
                                <Label>Model Name</Label>
                                <Input
                                    type="text"
                                    placeholder="Enter model name"
                                    value={modelName}
                                    onChange={(e) => setModelName(e.target.value)}
                                    valid={validation.modelName === true}
                                    invalid={validation.modelName === false}
                                />
                                <FormFeedback>Model name is required.</FormFeedback>
                            </Col>
                        </Row>

                        <Row className="mt-3">
                            <Col md="6">
                                <Label>License Plate</Label>
                                <Input
                                    type="text"
                                    placeholder="Enter license plate"
                                    value={licensePlate}
                                    onChange={(e) => setLicensePlate(e.target.value)}
                                    valid={validation.licensePlate === true}
                                    invalid={validation.licensePlate === false}
                                />
                                <FormFeedback>License plate is required.</FormFeedback>
                            </Col>

                            <Col md="6">
                                <Label>Vehicle Type</Label>
                                <Input
                                    type="select"
                                    value={typeId}
                                    onChange={(e) => setTypeId(e.target.value)}
                                    valid={validation.typeId === true}
                                    invalid={validation.typeId === false}
                                >
                                    <option value="">-- Select Type --</option>
                                    {vehicleTypes.map((t) => (
                                        <option key={t.id} value={t.id}>
                                            {t.displayname}
                                        </option>
                                    ))}
                                </Input>
                                <FormFeedback>Vehicle type is required.</FormFeedback>
                            </Col>
                        </Row>

                        <Row className="mt-3">
                            <Col md="6">
                                <Label>Year</Label>
                                <DatePicker
                                    selected={year}
                                    onChange={(date) => setYear(date)}
                                    showYearPicker
                                    dateFormat="yyyy"
                                    maxDate={Date.now()}
                                    className="form-control"
                                    placeholderText="Choose Year"
                                    popperClassName="modern-datepicker"
                                />
                                <FormFeedback>Year is required.</FormFeedback>
                            </Col>

                            <Col md="6">
                                <Label>Body Type</Label>
                                <Input
                                    type="select"
                                    value={bodyType}
                                    onChange={(e) => setBodyType(e.target.value)}
                                    valid={validation.bodyType === true}
                                    invalid={validation.bodyType === false}
                                >
                                    <option value="">-- Select Body Type --</option>
                                    {bodyTypes.map((t) => (
                                        <option key={t.id} value={t.name}>
                                            {t.name}
                                        </option>
                                    ))}
                                </Input>
                                <FormFeedback>Body type is required.</FormFeedback>
                            </Col>
                        </Row>

                        <Row className="mt-3">
                            <Col md="6">
                                <Label>Seats</Label>
                                <InputGroup>
                                    <Button
                                        type="button"
                                        color="primary"
                                        onClick={() => setSeats((prev) => Math.max(1, prev - 1))}
                                    >
                                        -
                                    </Button>
                                    <Input
                                        type="number"
                                        value={seats}
                                        readOnly
                                        className="text-center"
                                    />
                                    <Button
                                        type="button"
                                        color="primary"
                                        onClick={() => setSeats((prev) => prev + 1)}
                                    >
                                        +
                                    </Button>
                                </InputGroup>
                                <FormFeedback>Seats are required.</FormFeedback>
                            </Col>

                            <Col md="6">
                                <Label>Seat Rate</Label>
                                <Input
                                    type="number"
                                    placeholder="Enter seat rate"
                                    value={seatRate}
                                    onChange={(e) => setSeatRate(e.target.value)}
                                    valid={validation.seatRate === true}
                                    invalid={validation.seatRate === false}
                                />
                                <FormFeedback>Seat rate is required.</FormFeedback>
                            </Col>
                        </Row>

                        <Row className="mt-3">
                            <Col md="12">
                                <Label>Upload Images</Label>
                                <Input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={handleImageChange}
                                />
                                {previewUrls.length > 0 && (
                                    <div className="d-flex gap-2 mt-2 flex-wrap">
                                        {previewUrls.map((url, i) => (
                                            <div key={i} className="position-relative">
                                                <img
                                                    src={url}
                                                    alt="Preview"
                                                    style={{
                                                        width: "100px",
                                                        height: "100px",
                                                        objectFit: "cover",
                                                        borderRadius: "8px",
                                                        border: "2px solid #ddd",
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveImage(i)}
                                                    className="btn btn-danger btn-sm position-absolute top-0 end-0 m-1 rounded-circle"
                                                    style={{
                                                        width: "24px",
                                                        height: "24px",
                                                        padding: 0,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                    }}
                                                >
                                                    🗑
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Col>
                        </Row>

                        <Row className="mt-4">
                            <Col className="text-end">
                                <button className="btn btn-secondary me-2" onClick={onBacked}>
                                    ← Back
                                </button>
                                <Button type="submit" color="primary" disabled={loading}>
                                    {loading ? "Saving..." : "Submit"}
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </CardBody>
            </Card>
        </div>
    );
};

export default VehicleCreate;
